import { type HeaderContext } from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  EyeOff,
  Filter,
  SortAsc,
  SortDesc,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '~shared/shadcn/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~shared/shadcn/ui/popover';

interface DataTableColumnHeaderWithFilterProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  info: HeaderContext<TData, TValue>;
  title: string;
}

export function DataTableColumnHeaderWithFilter<TData, TValue>({
  info,
  title,
  className,
}: DataTableColumnHeaderWithFilterProps<TData, TValue>) {
  const { column } = info;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const uniqueValues = useMemo(() => {
    const values = column.getFacetedUniqueValues();
    return Array.from(values.keys()).filter(Boolean) as Array<string>;
  }, [column]);

  const filteredValues = uniqueValues.filter((value) => {
    return value.toString().toLowerCase().includes(search.toLowerCase());
  });

  const sortOrder = column.getIsSorted();
  const isFiltered = column.getIsFiltered();

  return (
    <div className={cn('flex items-center space-x-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='-ml-3 h-8 font-bold data-[state=open]:bg-accent'
          >
            <span>{title}</span>
            {sortOrder === 'desc' ? (
              <ArrowDown className='ml-2 size-4' />
            ) : sortOrder === 'asc' ? (
              <ArrowUp className='ml-2 size-4' />
            ) : isFiltered ? (
              <Filter className='ml-2 size-4' />
            ) : (
              <ChevronDown className='ml-2 size-4' />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[200px] p-0' align='start'>
          <Command>
            <CommandInput
              placeholder='Search values...'
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>No values found.</CommandEmpty>
              <CommandGroup heading='Sort'>
                <CommandItem
                  onSelect={() => {
                    column.toggleSorting(false);
                  }}
                  className='flex items-center gap-2'
                >
                  <SortAsc className='size-4' />
                  <span>Ascending</span>
                  {sortOrder === 'asc' && (
                    <ArrowUp className='ml-auto size-4' />
                  )}
                </CommandItem>
                <CommandItem
                  onSelect={() => {
                    column.toggleSorting(true);
                  }}
                  className='flex items-center gap-2'
                >
                  <SortDesc className='size-4' />
                  <span>Descending</span>
                  {sortOrder === 'desc' && (
                    <ArrowDown className='ml-auto size-4' />
                  )}
                </CommandItem>
                <CommandItem
                  onSelect={() => {
                    column.clearSorting();
                  }}
                  className='flex items-center gap-2'
                >
                  <X className='size-4' />
                  <span>Clear Sort</span>
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup heading='Actions'>
                <CommandItem
                  onSelect={() => {
                    column.toggleVisibility(false);
                  }}
                  className='flex items-center gap-2'
                >
                  <EyeOff className='size-4' />
                  <span>Hide Column</span>
                </CommandItem>
              </CommandGroup>
              <CommandSeparator />
              {filteredValues.length ? (
                <CommandGroup heading='Filter'>
                  {filteredValues.map((value) => (
                    <CommandItem
                      key={value}
                      onSelect={() => {
                        column.setFilterValue(
                          column.getFilterValue() === value ? undefined : value,
                        );
                      }}
                      className='flex items-center gap-2'
                    >
                      <span>{value}</span>
                      {column.getFilterValue() === value && (
                        <Check className='ml-auto size-4' />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
